// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback } from 'react';
import { InstrumentId } from '@daml.js/splice-api-token-holding-v1-1.0.0/lib/Splice/Api/Token/HoldingV1';
import FormDialog from '../../../components/Dialog/FormDialog';
import { useParties } from '../../../contexts/PartiesContext';
import useRequestBridgeUserAgreement from '../../../hooks/mutations/bridge/onboarding/useRequestBridgeUserAgreement';
import useMutate from '../../../hooks/mutations/useMutate';
import useBridgeUserAgreementRequests from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreementRequests';
import useBridgeUserAgreements from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreements';
import { ERR_REQ_BUA_SVC, SUC_REQ_BUA_SVC } from '../../../utils/strings';
import { INSTRUMENT_IDENTIFIER } from '../../../utils/constants';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestBridgeUserAgreementDialog = ({ open, onClose }: DialogProps) => {
  const requestBridgeUserAgreement = useRequestBridgeUserAgreement();
  const mutate = useMutate();

  // Check if service is already requested or provided
  const { operator, crossChainRepresentative, bridgeOperator, party } = useParties();
  const bridgeUserAgreements = useBridgeUserAgreements();
  const bridgeUserAgreementRequests = useBridgeUserAgreementRequests();
  const hasService = !!bridgeUserAgreements.data?.find(
    (r) => r.payload.user === party && r.payload.bridgeOperator === bridgeOperator,
  );
  const hasPendingRequest = !!bridgeUserAgreementRequests.data?.find(
    (r) => r.payload.user === party && r.payload.bridgeOperator === bridgeOperator,
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      crossChainRepresentative,
      operator,
      bridgeOperator,
      user: party,
      instrumentId: {
        admin: crossChainRepresentative,
        id: INSTRUMENT_IDENTIFIER,
      } as InstrumentId,
    };
    const res = await mutate(requestBridgeUserAgreement, payload, SUC_REQ_BUA_SVC, ERR_REQ_BUA_SVC);
    handleClose();
    return res;
  }, [requestBridgeUserAgreement, handleClose, mutate]);

  const isLoading = false;
  if (isLoading) return null;

  const disableSubmit = hasService || hasPendingRequest;
  return (
    <FormDialog
      open={open}
      title="Request User Agreement"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    />
  );
};
